import React, { useState, useEffect, useContext, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Row, Col, Button, Modal, Form } from "react-bootstrap";
import { XCircle } from "react-bootstrap-icons";
import { clearActiveExercise } from "../../../redux/slices/exerciseSlice";
import {
  setExerciseWords,
  fetchUserVocabulary,
  updateWordProgress,
} from "../../../redux/slices/userVocabularySlice";
import { UserContext } from "../../user/UserContext";
import { updateUserItem } from "../../../utils/updateTableItem";
import handlePronunciation from "../../../utils/handlePronunciation";
import "./WordConstructor.css";

export default function WordConstructor() {
  const dispatch = useDispatch(); // Redux dispatch function
  const { userAttributes } = useContext(UserContext); // Fetch user attributes from UserContext
  const { userWords, exerciseWords, loading } = useSelector(
    (state) => state.userVocabulary
  ); // Fetch user words and exercise words from Redux store

  const [currentWordIndex, setCurrentWordIndex] = useState(0); // Track the index of the current word
  const [currentWord, setCurrentWord] = useState(""); // Store the current word object
  const [selectedLetters, setSelectedLetters] = useState(""); // Store the selected letters as a string
  const [poolLetters, setPoolLetters] = useState([]); // Store the pool of available letters for the current word
  const [currentLetterIndex, setCurrentLetterIndex] = useState(0); // Track the index of the current letter being selected
  const [mistakes, setMistakes] = useState(0); // Track the number of mistakes made
  const [showModal, setShowModal] = useState(false); // State to show/hide the exercise completion modal
  const [isChecked, setIsChecked] = useState(false); // State to control checkbox

  const progressUpdatesRef = useRef([]); // Ref to store progress updates without causing re-renders

  // Shuffle the letters of a word to randomize their order
  const shuffleArray = (array) => {
    let currentIndex = array.length,
      randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }
    return array;
  };

  // **Function to count occurrences of each letter in the pool**
  const countLetterOccurrences = (letters) => {
    const letterCount = {};
    letters.forEach((letter) => {
      letterCount[letter] = (letterCount[letter] || 0) + 1;
    });
    return letterCount;
  };

  // Prepare a new exercise by selecting the words with the lowest progress
  const prepareNewExercise = () => {
    if (userWords.length > 0) {
      const wordsToTrain = [...userWords]
        .sort((a, b) => a.progress - b.progress) // Sort by progress (lowest first)
        .slice(0, 5); // Select the first 5 words
      dispatch(setExerciseWords(wordsToTrain)); // Dispatch the selected words to Redux
    }
  };

  // Commit all progress updates to DynamoDB and the Redux store
  const commitAllProgressUpdates = async () => {
    try {
      console.log(`Committing all progress updates...`);
      for (const update of progressUpdatesRef.current) {
        await updateUserItem(update.id, null, update.newProgress); // Update progress in DynamoDB
      }
      progressUpdatesRef.current.forEach((update) =>
        dispatch(updateWordProgress(update))
      ); // Dispatch progress updates to Redux store
      progressUpdatesRef.current = []; // Clear progress updates after committing
    } catch (error) {
      console.error("Failed to update progress in DB", error);
    }
  };

  // Load the next word in the exercise based on the word index
  const loadNextWord = (wordIndex) => {
    if (wordIndex < exerciseWords.length) {
      const nextWord = exerciseWords[wordIndex]; // Get the next word from the exerciseWords array
      setSelectedLetters(""); // Reset selected letters (string)
      setMistakes(0); // Reset mistakes for the next word
      setCurrentLetterIndex(0); // Reset the current letter index
      setPoolLetters(shuffleArray([...nextWord.word])); // Shuffle and set the pool of letters
      setCurrentWord(nextWord); // Set the new current word
      setCurrentWordIndex(wordIndex); // Update the current word index
    } else {
      commitAllProgressUpdates(); // Commit all progress updates at the end of the exercise
      setShowModal(true); // Show the modal when the exercise is finished
    }
  };

  // Update the progress of the current word
  const updateProgress = (change) => {
    const currentWordObj = exerciseWords[currentWordIndex]; // Get the current word object
    const newProgress = Math.max(
      1,
      Math.min(100, currentWordObj.progress + change)
    ); // Calculate the new progress value (ensure it stays between 1 and 100)

    console.log(
      `Updating progress for word: ${currentWordObj.word}, Progress change: ${change}, New progress: ${newProgress}`
    );

    // Store the progress update in the ref for later commitment
    progressUpdatesRef.current = [
      ...progressUpdatesRef.current,
      { id: currentWordObj.id, newProgress },
    ];
  };

  // Handle the user's letter click and validate it against the current word
  const handleLetterClick = async (letter) => {
    if (poolLetters.includes(letter)) {
      // Check if the letter matches the current letter in the word
      if (letter === currentWord.word[currentLetterIndex]) {
        setSelectedLetters(
          (prevSelectedLetters) => prevSelectedLetters + letter
        ); // Add the correct letter to the selected letters string

        // Remove one occurrence of the letter from the pool
        setPoolLetters((prevPoolLetters) => {
          const index = prevPoolLetters.indexOf(letter);
          if (index > -1) {
            const newPool = [...prevPoolLetters];
            newPool.splice(index, 1);
            return newPool;
          }
          return prevPoolLetters;
        });

        const nextLetterIndex = currentLetterIndex + 1;
        if (nextLetterIndex === currentWord.word.length) {
          updateProgress(10); // Increase progress when the word is correctly filled
          if (isChecked) {
            // Await the pronunciation to complete before loading the next word
            await handlePronunciation(currentWord.word); // Pronounces the current word
          }
          loadNextWord(currentWordIndex + 1); // Move to the next word
        } else {
          setCurrentLetterIndex(nextLetterIndex); // Move to the next letter
        }
      } else {
        // Handle mistakes (wrong letter)
        setMistakes((mistakes) => {
          if (mistakes + 1 >= 3) {
            updateProgress(-5); // Decrease progress after 3 mistakes
            loadNextWord(currentWordIndex + 1); // Load the next word after 3 mistakes
          }
          return mistakes + 1;
        });

        setTimeout(() => {
          // Reset the selected letters string if there are mistakes
          setSelectedLetters((prevSelectedLetters) =>
            prevSelectedLetters.slice(0, currentLetterIndex)
          );
        }, 300);
      }
    }
  };

  // Handle key presses for letter input
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKeyPress = (event) => {
    const pressedKey = event.key.toLowerCase(); // Convert the pressed key to lowercase
    if (poolLetters.includes(pressedKey)) {
      handleLetterClick(pressedKey); // Call the letter click handler with the pressed key
    }
  };

  // Handle the "Don't Know" button click (decrease progress and move to the next word)
  // eslint-disable-next-line no-unused-vars
  const handleDontKnow = () => {
    updateProgress(-5); // Decrease progress when the user doesn't know the word
    loadNextWord(currentWordIndex + 1); // Move to the next word
  };

  // Handle restarting the exercise (Train Again)
  const handleTrainAgain = () => {
    setCurrentWordIndex(0); // Reset the word index to 0
    setCurrentWord(exerciseWords[0]); // Reset the current word to the first one
    setShowModal(false); // Hide the modal
  };

  // Fetch user vocabulary if no words are available
  useEffect(() => {
    if (userWords.length === 0 && !loading) {
      const loadData = async () => {
        try {
          await dispatch(fetchUserVocabulary({ owner: userAttributes.sub })); // Fetch user vocabulary from the API
        } catch (error) {
          console.error("Error in loadData:", error);
        }
      };
      loadData();
    }
  }, [dispatch, userAttributes.sub, userWords.length, loading]);

  // Prepare a new exercise when the user's words change
  useEffect(() => {
    prepareNewExercise(); // Prepare a new exercise with the fetched words
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userWords]);

  // Load the first word in the exercise when exerciseWords change, unless the modal is open
  useEffect(() => {
    if (exerciseWords.length > 0 && !showModal) {
      loadNextWord(0); // Load the first word if the modal is not shown
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseWords, showModal]);

  // Add a keydown event listener to handle key presses for letter selection
  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress); // Clean up the event listener
    };
  }, [poolLetters, handleKeyPress]);

  function handleCheckboxChange() {
    setIsChecked(!isChecked); // Toggle the checkbox state
  }

  // **Get counts of letters in the pool**
  const letterCounts = countLetterOccurrences(poolLetters);

  // **Get unique letters**
  const uniqueLetters = Object.keys(letterCounts);

  return (
    <Container className="word-constructor mt-5">
      {/* Header */}
      <Row>
        <Col md={3} className="d-none d-md-block">
          <h3 className="mb-1">Word Constructor</h3>
          <p className="text-muted">Vocabulary Training</p>
        </Col>
        <Col md={6} className="text-center">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <Form className="me-3">
                <Form.Check
                  type="checkbox"
                  id="autopronunciation-checkbox"
                  label="Autopronunciation"
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                />
              </Form>
            </div>
            <h5>
              {currentWordIndex + 1}/{exerciseWords.length}
            </h5>
          </div>
        </Col>
        <Col md={3} className="d-flex justify-content-end">
          <button
            className="btn btn-sm position-absolute"
            style={{
              border: "none",
              background: "none",
              padding: "0.5rem 1rem",
              marginBottom: "5px",
              borderRadius: "0.375rem",
            }}
            onClick={() => dispatch(clearActiveExercise())}
          >
            <XCircle size={24} className="text-muted" />
          </button>
        </Col>
      </Row>

      {/* Display translation */}
      <Row className="justify-content-center mt-4">
        <Col md={8}>
          <div className="d-flex align-items-center justify-content-center">
            <h4>{currentWord.translation}</h4> {/* Display the translation */}
          </div>
        </Col>
      </Row>

      {/* Word input field (letters selected) */}
      <Row className="justify-content-center mt-4">
        <Col md={8}>
          <div className="d-flex align-items-center justify-content-center">
            <input
              type="text"
              className="text-center invisible-input" // Add custom class for styling
              value={selectedLetters}
              readOnly // Prevent user from typing directly
              tabIndex="-1" // Prevent focusing on the input (no cursor)
            />
          </div>
        </Col>
      </Row>

      {/* Letters pool */}
      <Row className="justify-content-center mt-3">
        <Col md={8} className="d-flex justify-content-center">
          <div className="letter-pool p-2">
            <div className="d-flex flex-wrap justify-content-center">
              {uniqueLetters.map((letter, index) => (
                <Button
                  key={index}
                  variant="primary"
                  className="letter-button mx-1 position-relative"
                  style={{ overflow: "visible" }}
                  onClick={() => handleLetterClick(letter)}
                >
                  {letter}
                  {/* Badge for duplicates */}
                  {letterCounts[letter] > 1 && (
                    <span
                      className="badge position-absolute top-0 start-100 translate-middle bg-danger"
                      style={{
                        borderRadius: "50%",
                        fontSize: "0.8rem",
                        width: "1.2rem",
                        height: "1.2rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {letterCounts[letter]}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </div>
        </Col>
      </Row>

      {/* Mistake Dots */}
      <Row className="justify-content-center mt-3">
        <Col md={8} className="d-flex justify-content-center">
          <div className="d-flex mistake-dots">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={`dot ${i < mistakes ? "filled" : ""}`} // Fill the dot based on mistakes
              />
            ))}
          </div>
        </Col>
      </Row>
      <Row className="justify-content-center mt-4">
        <Col md={8} className="d-flex justify-content-between">
          <Button variant="outline-secondary">
            <i className="bi bi-trash" />
          </Button>
          <Button variant="outline-secondary" onClick={handleDontKnow}>
            Don't know 🙁
          </Button>
        </Col>
      </Row>

      {/* Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Exercise Complete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Congratulations! You've completed the exercise.</p>
          <p>What would you like to do next?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => dispatch(clearActiveExercise())}
          >
            Back to exercises
          </Button>
          <Button variant="primary" onClick={handleTrainAgain}>
            Train again
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
